import { useMutation } from '@apollo/client';
import isEqual from 'lodash/isEqual';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context';
import {
  CreateAudienceSharesDocument,
  type CreateAudienceSharesMutation,
  type CreateAudienceSharesMutationVariables,
} from '../../../graphql/mutation/audiences/CreateAudienceShares.generated';
import {
  DeleteAudienceSharesDocument,
  type DeleteAudienceSharesMutation,
} from '../../../graphql/mutation/audiences/DeleteAudienceShares.generated';
import {
  UpdateAudienceSharesDocument,
  type UpdateAudienceSharesMutation,
} from '../../../graphql/mutation/audiences/UpdateAudienceShares.generated';
import { CreateLogDocument } from '../../../graphql/mutation/error/createLog.generated';
import type { GetAudienceQuery } from '../../../graphql/query/audiences/GetAudience.generated';
import { prepareSharesPayloads } from '../AudienceForm/hooks/utils/sharesPayload';
import type { AudienceShares } from '../types';
import { mapAudienceShareToForm } from '../utils/mapAudienceShareToForm';

const PERCENT_TO_DECIMAL_FACTOR = 100;

export const mapAudienceToCreateShareMutation = (shares: AudienceShares['audience_share']) => {
  const result: CreateAudienceSharesMutationVariables['input'] = [];

  shares.forEach(share => {
    share.recipient_account_id.forEach(accountId => {
      if (share.revenue_method === 'PoM') {
        result.push({
          audience_external_openaudience_id: share.audience_external_openaudience_id,
          recipient_account_id: accountId,
          revenue_method: 'PoM',
          third_party_revenue: share.isFeeEnabled
            ? (parseFloat(share.value) / PERCENT_TO_DECIMAL_FACTOR).toString()
            : '0',
        });
      } else {
        result.push({
          audience_external_openaudience_id: share.audience_external_openaudience_id,
          recipient_account_id: accountId,
          revenue_method: 'CPM',
          cpm_cap: share.isFeeEnabled ? share.value : '0',
        });
      }
    });
  });

  return result;
};

export const useShareAudience = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();

  const [createAudienceShares, { loading: createLoading }] = useMutation(CreateAudienceSharesDocument, {
    refetchQueries: ['GetAudienceShares', 'GetAudience'],
  });

  const [updateAudienceShares, { loading: updateLoading }] = useMutation(UpdateAudienceSharesDocument, {
    refetchQueries: ['GetAudienceShares', 'GetAudience'],
  });

  const [deleteAudienceShares, { loading: deleteLoading }] = useMutation(DeleteAudienceSharesDocument, {
    refetchQueries: ['GetAudienceShares', 'GetAudience'],
  });

  const [pushLog] = useMutation(CreateLogDocument);

  const shareAudience = useCallback(
    async (
      formValues: AudienceShares,
      audienceId: string,
      initialAudienceShares: NonNullable<GetAudienceQuery['audience_by_pk']>['audience_share']
    ) => {
      const updatedShares = mapAudienceToCreateShareMutation(formValues.audience_share);
      const initialShares = mapAudienceToCreateShareMutation(mapAudienceShareToForm(audienceId, initialAudienceShares));

      if (!isEqual(updatedShares, initialShares)) {
        const initialSharesRecipientToShareUid = new Map<string, string>();

        initialAudienceShares.forEach(share => {
          if (share?.recipient_account_id && share?.uid) {
            initialSharesRecipientToShareUid.set(share.recipient_account_id, share.uid);
          }
        });

        const { audienceShareCreatePayload, audienceShareUpdatePayload, audienceShareDeletePayload } =
          prepareSharesPayloads(initialShares, updatedShares, initialSharesRecipientToShareUid);

        try {
          let audienceShareDelete: DeleteAudienceSharesMutation['audienceShareDelete'] = [];

          if (audienceShareDeletePayload.length) {
            const result = await deleteAudienceShares({ variables: { input: audienceShareDeletePayload } });

            audienceShareDelete = result.data?.audienceShareDelete ?? [];
          }

          let audienceShareUpdate: UpdateAudienceSharesMutation['audienceShareUpdate'] = [];

          if (audienceShareUpdatePayload.length) {
            const updateResp = await updateAudienceShares({
              variables: { input: audienceShareUpdatePayload },
            });

            audienceShareUpdate = updateResp.data?.audienceShareUpdate ?? [];
          }

          let audienceShareCreate: CreateAudienceSharesMutation['audienceShareCreate'] = [];

          if (audienceShareCreatePayload.length) {
            const createResp = await createAudienceShares({
              variables: { input: audienceShareCreatePayload },
            });

            audienceShareCreate = createResp.data?.audienceShareCreate ?? [];
          }

          const deleteErrors = audienceShareDelete.filter(response => response && response.__typename === 'Error');
          const updateErrors = audienceShareUpdate.filter(response => response && response.__typename === 'Error');
          const createErrors = audienceShareCreate.filter(response => response && response.__typename === 'Error');

          const results = [...audienceShareDelete, ...audienceShareUpdate, ...audienceShareCreate];
          const errors = [...deleteErrors, ...updateErrors, ...createErrors];

          const getWarningMessage = ({
            audienceShareDelete,
            deleteErrors,
            audienceShareUpdate,
            updateErrors,
            audienceShareCreate,
            createErrors,
          }) => {
            const deleteShareCount = audienceShareDelete.length;
            const updateShareCount = audienceShareUpdate.length;
            const createShareCount = audienceShareCreate.length;

            const deleteErrorCount = deleteErrors.length;
            const updateErrorCount = updateErrors.length;
            const createErrorCount = createErrors.length;

            const successfulDeletes = deleteShareCount - deleteErrorCount;
            const successfulUpdates = updateShareCount - updateErrorCount;
            const successfulCreates = createShareCount - createErrorCount;

            const deleteCaption =
              deleteShareCount > 0 ? `${t('Deleted')}: ${successfulDeletes}/${deleteShareCount}.` : '';
            const updateCaption =
              updateShareCount > 0 ? `${t('Updated')}: ${successfulUpdates}/${updateShareCount}.` : '';
            const createCaption =
              createShareCount > 0 ? `${t('Created')}: ${successfulCreates}/${createShareCount}.` : '';

            return `${t('Audience was shared partially.')} ${deleteCaption} ${updateCaption} ${createCaption}`.replace(
              / {2,}/g,
              ' '
            );
          };

          const warningMessage = getWarningMessage({
            audienceShareDelete,
            deleteErrors,
            audienceShareUpdate,
            updateErrors,
            audienceShareCreate,
            createErrors,
          });

          if (errors.length === 0) {
            enqueueSnackbar(t('Audience shared successfully'), { variant: 'success' });
          } else {
            if (errors.length < results.length) {
              enqueueSnackbar(warningMessage, { variant: 'warning' });
            } else {
              throw new Error('Cannot share audience');
            }
          }
        } catch (_) {
          await pushLog({
            variables: {
              location: window.location.href,
              msg: 'Failed to share audiences',
              name: currentUser?.displayName,
              email: currentUser?.email,
            },
          });

          enqueueSnackbar(t('Failed to share the audience'), { variant: 'error' });
        }
      }
    },
    [enqueueSnackbar, t, currentUser, createAudienceShares, updateAudienceShares, deleteAudienceShares, pushLog]
  );

  const shareLoading = createLoading || updateLoading || deleteLoading;

  return { shareAudience, shareLoading };
};
